/**
 * 📦 useArchivedEmployees Hook
 * Manages archived employees state, fetching, filtering, and actions
 */

import { useState, useMemo } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";

const API_URL = process.env.REACT_APP_BASE_URL;

  // Sanitize strings to avoid sending suspicious payloads that trigger WAF/IDS
  const sanitizeString = (val) => {
    if (val === undefined || val === null) return "";
    // Remove common SQL injection characters and control chars, keep basic punctuation
    return String(val)
      .replace(/(--|;|\bOR\b|\bAND\b|'|"|\\|\/) /gi, "")
      .replace(/[\x00-\x1F\x7F]/g, "")
      .trim();
  };

  const sanitizePhone = (val) => {
    if (val === undefined || val === null) return "";
    // Keep only digits and leading +, limit length to 30
    const s = String(val).replace(/[^+\d]/g, "");
    return s.slice(0, 30);
  };

/**
 * Get user initials from name
 */
const getUserInitials = (name) => {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const useArchivedEmployees = () => {
  const { profile } = useProfile();
  const clientName = profile?.clientName ?? null;
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Archive filters
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveFilterCompany, setArchiveFilterCompany] = useState("");
  const [archiveFilterCompanySearch, setArchiveFilterCompanySearch] = useState("");
  const [archiveFilterWorkingCompany, setArchiveFilterWorkingCompany] = useState("");
  const [archiveFilterWorkingCompanySearch, setArchiveFilterWorkingCompanySearch] = useState("");
  const [archiveFilterDepartment, setArchiveFilterDepartment] = useState("");
  const [archiveFilterDepartmentSearch, setArchiveFilterDepartmentSearch] = useState("");
  const [archiveFilterDateFrom, setArchiveFilterDateFrom] = useState("");
  const [archiveFilterDateTo, setArchiveFilterDateTo] = useState("");

  /**
   * Fetch archived employees from API
   */
  const fetchArchivedEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get client name from profile
      // clientName is already resolved from useProfile above

      const response = await apiFetch(`${API_URL}/employees/archived`, {
        method: "GET",
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.error || `Failed to fetch archived employees (${response.status})`);
      }
      const archivedData = responseData.employees || responseData;

      console.log(`✅ Received ${archivedData.length} archived employees`);

      // Filter on frontend based on client name
      let filteredArchivedData = archivedData;

      if (clientName && clientName.toLowerCase() !== "infinity") {
        // Filter for specific client
        console.log(`🔍 Filtering archived employees for client: ${clientName}`);
        filteredArchivedData = archivedData.filter(
          (emp) => emp.clientName?.toLowerCase() === clientName.toLowerCase()
        );
        console.log(
          `✅ Filtered to ${filteredArchivedData.length} archived employees for ${clientName}`
        );
      }

      // Map archived employees with proper formatting
      const mappedArchivedEmployees = filteredArchivedData.map((emp) => ({
        id: emp.infinityCode || emp.id,
        infinityCode: emp.infinityCode || emp.id,
        name: emp.sr || "N/A",
        arabicName: emp.arabicName || "",
        gender: emp.gender || "",
        laborMode: emp.laborMode || "N/A",
        department: emp.department?.name ?? emp.location ?? "N/A",
        position: emp.position ?? "N/A",
        email: emp.email || "N/A",
        phone: emp.phoneNumber || "N/A",
        salary: emp.netSalary || 0,
        joinDate: emp.hiringDate,
        status: "Archived",
        company: emp.clientName || "N/A",
        workingCompany: emp.workingCompany || "",
        archivedDate:
          emp.archivedDate || emp.created_at || new Date().toISOString(),
        avatar: getUserInitials(emp.sr),
      }));

      // Update archived employees state
      setArchivedEmployees(mappedArchivedEmployees || []);
      setArchivedCount(mappedArchivedEmployees?.length || 0);

      console.log(
        `✅ Successfully mapped ${mappedArchivedEmployees.length} archived employees`
      );
    } catch (err) {
      // Log error - don't show error message as endpoint might not exist
      console.error("❌ Failed to fetch archived employees:", err);
      setError(err.message);
      setArchivedEmployees([]);
      setArchivedCount(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Restore employee from archive
   */
  const restoreEmployee = async (employeeId) => {
    try {
      setLoading(true);

      // Find the archived employee
      const employeeToRestore = archivedEmployees.find(
        (emp) => emp.id === employeeId
      );

      if (!employeeToRestore) {
        throw new Error("Employee not found");
      }

      // Use infinityCode (the actual unique employee code) instead of id (row id)
      const infinityCode =
        employeeToRestore.infinityCode || employeeToRestore.id;

      // Remove archived properties and set status to active
      const restoredEmployee = {
        ...employeeToRestore,
        status: "Active",
      };
      delete restoredEmployee.archivedDate;

      // Sanitize data to avoid triggering WAF/IDS when sending archived fields
      const sanitizedEmployee = {
        ...restoredEmployee,
        name: sanitizeString(restoredEmployee.name),
        arabicName: sanitizeString(restoredEmployee.arabicName),
        email: sanitizeString(restoredEmployee.email),
        phone: sanitizePhone(restoredEmployee.phone),
      };

      // Restore to active employees using infinityCode
      await apiFetch(`${API_URL}/employees/restore/${infinityCode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employeeId: infinityCode,
          employeeData: sanitizedEmployee,
        }),
      });

      // Update local state - remove from archived list
      setArchivedEmployees(
        archivedEmployees.filter((emp) => emp.id !== employeeId)
      );
      setArchivedCount((prev) => prev - 1);

      return { success: true, employee: restoredEmployee };
    } catch (err) {
      console.error("Failed to restore employee:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Restore all archived employees
   */
  const restoreAllEmployees = async () => {
    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Restore all archived employees one by one
      for (const emp of archivedEmployees) {
        try {
          const result = await restoreEmployee(emp.id);
          if (result.success) successCount++;
          else failCount++;
        } catch (err) {
          console.error(`Failed to restore employee ${emp.id}:`, err);
          failCount++;
        }
      }

      return { successCount, failCount };
    } catch (err) {
      console.error("Failed to restore all employees:", err);
      setError(err.message);
      return { successCount: 0, failCount: archivedEmployees.length };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Permanently delete all archived employees
   */
  const deleteAllArchived = async () => {
    try {
      setLoading(true);

      // Call API to clear archived employees
      await apiFetch(`${API_URL}/employees/archived`, {
        method: "DELETE",
      });

      // Clear local state
      setArchivedEmployees([]);
      setArchivedCount(0);

      return { success: true };
    } catch (err) {
      console.error("Failed to delete all archived employees:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Permanently delete a single archived employee (uses infinityCode, not row id)
   */
  const deleteArchivedEmployee = async (employeeId) => {
    try {
      setLoading(true);

      const employeeToDelete = archivedEmployees.find(
        (emp) => emp.id === employeeId
      );
      const infinityCode = employeeToDelete?.infinityCode || employeeId;

      await apiFetch(`${API_URL}/employees/archived/${infinityCode}`, {
        method: "DELETE",
      });

      setArchivedEmployees(
        archivedEmployees.filter((emp) => emp.id !== employeeId)
      );
      setArchivedCount((prev) => prev - 1);

      return { success: true };
    } catch (err) {
      console.error("Failed to delete archived employee:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear all archive filters
   */
  const clearFilters = () => {
    setArchiveSearch("");
    setArchiveFilterCompany("");
    setArchiveFilterCompanySearch("");
    setArchiveFilterWorkingCompany("");
    setArchiveFilterWorkingCompanySearch("");
    setArchiveFilterDepartment("");
    setArchiveFilterDepartmentSearch("");
    setArchiveFilterDateFrom("");
    setArchiveFilterDateTo("");
  };

  /**
   * Filtered archived employees based on search and filters
   */
  const filteredArchivedEmployees = useMemo(() => {
    return archivedEmployees.filter((emp) => {
      // Search filter
      if (archiveSearch) {
        const search = archiveSearch.toLowerCase();
        const matchesSearch =
          (emp.name || "").toLowerCase().includes(search) ||
          (emp.arabicName || "").toLowerCase().includes(search) ||
          (emp.email || "").toLowerCase().includes(search) ||
          String(emp.id || "").toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Company filter
      if (archiveFilterCompany && emp.company !== archiveFilterCompany) {
        return false;
      }

      // Working company filter
      if (
        archiveFilterWorkingCompany &&
        emp.workingCompany !== archiveFilterWorkingCompany
      ) {
        return false;
      }

      // Department filter
      if (
        archiveFilterDepartment &&
        emp.department !== archiveFilterDepartment
      ) {
        return false;
      }

      // Date range filter
      if (archiveFilterDateFrom || archiveFilterDateTo) {
        const archivedDate = emp.archivedDate
          ? new Date(emp.archivedDate)
          : null;
        if (!archivedDate) return false;

        if (archiveFilterDateFrom) {
          const fromDate = new Date(archiveFilterDateFrom);
          if (archivedDate < fromDate) return false;
        }

        if (archiveFilterDateTo) {
          const toDate = new Date(archiveFilterDateTo);
          toDate.setHours(23, 59, 59, 999); // Include the entire day
          if (archivedDate > toDate) return false;
        }
      }

      return true;
    });
  }, [
    archivedEmployees,
    archiveSearch,
    archiveFilterCompany,
    archiveFilterWorkingCompany,
    archiveFilterDepartment,
    archiveFilterDateFrom,
    archiveFilterDateTo,
  ]);

  /**
   * Get unique filter options from archived employees
   */
  const filterOptions = useMemo(() => {
    return {
      companies: [
        ...new Set(
          archivedEmployees
            .map((e) => e.company)
            .filter((v) => v && v !== "N/A")
        ),
      ],
      workingCompanies: [
        ...new Set(archivedEmployees.map((e) => e.workingCompany).filter(Boolean)),
      ],
      departments: [
        ...new Set(
          archivedEmployees
            .map((e) => e.department)
            .filter((v) => v && v !== "N/A")
        ),
      ],
    };
  }, [archivedEmployees]);

  /**
   * Count employees archived this month
   */
  const archivedThisMonth = useMemo(() => {
    const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return archivedEmployees.filter(
      (emp) => emp.archivedDate && new Date(emp.archivedDate) > oneMonthAgo
    ).length;
  }, [archivedEmployees]);

  return {
    // State
    archivedEmployees,
    archivedCount,
    loading,
    error,

    // Filters
    archiveSearch,
    setArchiveSearch,
    archiveFilterCompany,
    setArchiveFilterCompany,
    archiveFilterCompanySearch,
    setArchiveFilterCompanySearch,
    archiveFilterWorkingCompany,
    setArchiveFilterWorkingCompany,
    archiveFilterWorkingCompanySearch,
    setArchiveFilterWorkingCompanySearch,
    archiveFilterDepartment,
    setArchiveFilterDepartment,
    archiveFilterDepartmentSearch,
    setArchiveFilterDepartmentSearch,
    archiveFilterDateFrom,
    setArchiveFilterDateFrom,
    archiveFilterDateTo,
    setArchiveFilterDateTo,

    // Filtered data
    filteredArchivedEmployees,
    filterOptions,
    archivedThisMonth,

    // Actions
    fetchArchivedEmployees,
    restoreEmployee,
    restoreAllEmployees,
    deleteAllArchived,
    deleteArchivedEmployee,
    clearFilters,
  };
};

